import { all, takeLatest } from 'redux-saga/effects';
import { fetchMoreEvents, getVdeEvents } from '../reducers/eventDetailsSlice';
import logoutSaga from '../components/LogoutButton/saga';
import handleFarmDetails from '../components/FarmDetails/saga';
import {
  eventPageDetailsSaga,
  getMoreEvents,
} from '../containers/EventsPage/sagas/saga';
import selectedEventPageDetailsSaga from '../containers/EventsPage/sagas/selectedEventSaga';
import domainsSaga from '../components/DomainDetails/saga';
import getEntityInformation from '../common/components/FarmAndEntityDetailsHeader/saga';
import domainComponentsSaga from '../components/DomainElements/saga';
import errorCodeSaga from '../common/redux/errorCodeHandler/saga';
import eventDropdownLevelsSaga from '../common/redux/eventDropdownLevel/saga';
import canbusTroubleshootSaga from '../common/redux/canbusTroubleshooter/saga';
import googleAnalyticTrackingSaga from '../common/redux/googleAnalyticTracking/saga';
import canbusTroubleshootTutorialSaga from '../common/redux/canbusTroubleshooterTutorial/saga';
import entityKpiDetailsSaga from '../common/components/KpiGrid/saga';
import alarmTablesSaga from '../common/components/TableGrid/saga';
import featureFlagSaga from '../common/redux/FeatureFlag/saga';
import entityStatusSaga from '../components/EntityState/saga';
import entityBackupSettingsSaga from '../containers/BackupSettings/saga';
import createEntityBackupSettingsSaga from '../containers/BackupSettings/createSaga';
import updateEntityBackupSettingsSaga from '../containers/BackupSettings/updateSaga';
import restoreEntityBackupSettingsSaga from '../containers/BackupSettings/restoreSaga';
import entitySettingsSaga from '../containers/Settings/sagas/settingsSaga';
import entitySettingsOptionsSaga from '../containers/Settings/sagas/settingsOptionsSaga';
import applicationCacheSaga from '../common/components/CacheBust/saga';
import dataChartSaga from '../components/DataCharts/sagas/dataChartSaga';
import dataChartEventsSaga from '../components/DataCharts/sagas/dataChartEventsSaga';
import dataChartConfigurationSaga from '../components/DataCharts/sagas/dataConfigChartSaga';
import vectorDataChartSaga from '../components/Vector/sagas/vectorDataChartSaga';
import vectorConfigurationSaga from '../components/Vector/sagas/vectorConfigurationSaga';
import updateLanguageSaga from '../common/components/Translation/saga';
import compositeStateSaga from '../components/DataCharts/sagas/compositeStateSaga';
import milkJarSaga from '../components/DataCharts/sagas/milkJarSaga';
import eventsLevelMenuOptionsSaga from '../common/redux/eventDropdownLevelMenuOptions/saga';
import { farmsSaga, fetchKpisSaga } from '../components/FarmsTable/saga';
import updatesSaga from '../components/UpdatesDetails/updatesSaga';
import updateHistorySaga from '../components/UpdatesHistory/sagas/updateHistorySaga';
import updateHistorySagaForSystem from '../components/UpdatesHistory/sagas/updateHistorySagaForSystem';
import kpiSaga from '../components/PerformanceIndicators/kpiSaga';
import translationSaga from '../common/i18n/translationSaga';
import { postLoginActions } from './postLoginSaga';

// Api's
import { api as logoutApi } from '../api/lelyBackend/logoutApi';
import { api as updatesApi } from '../api/lelyBackend/updatesApi';
import { api as farmDetailsApi } from '../api/lelyBackend/farmDetailsApi';
import { api as alarmsApi } from '../api/lelyBackend/alarmsApi';
import { api as eventsApi } from '../api/lelyBackend/eventsApi';
import { api as eventsPageApi } from '../api/lelyBackend/eventsPageApi';
import { api as domainsApi } from '../api/lelyBackend/domainsApi';
import { api as logsApi } from '../api/lelyBackend/logsApi';
import { api as settingsApi } from '../api/lelyBackend/settingsApi';
import { api as troubleshootApi } from '../api/lelyBackend/troubleshootApi';
import { api as entityStatusApi } from '../api/lelyBackend/entityStatusApi';
import { api as entityKpiDetailsApi } from '../api/lelyBackend/entityKpiDetailsApi';
import { api as backupsApi } from '../api/lelyBackend/backupsApi';
import { api as dataChartsApi } from '../api/lelyBackend/dataChartsApi';
import { api as compositeStatesApi } from '../api/lelyBackend/compositeStateApi';
import { api as vectorConfigurationApi } from '../api/lelyBackend/vectorConfigurationApi';
import { api as updateLelyPreferredLanguage } from '../api/lelyBackend/translationApi';
import { api as updateHistoryApi } from '../api/lelyBackend/updateHistoryApi';
import { api as kpiApi } from '../api/lelyBackend/kpiDataApi';
import { api as entityInfoApi } from '../api/lelyBackend/entityInfoApi';
import { api as userDetailsApi } from '../api/lelyBackend/userDetails';

// Mock api's
import { api as mockLogoutApi } from '../api/mockApi/logoutApi';
import { api as mockUpdateHistoryApi } from '../api/mockApi/updateHistoryApi';
import { setNetworkError } from '../reducers/networkErrorSlice';
import getBackupProgressSaga from '../containers/BackupSettings/listenForBackupCreatonSaga';
import { getFarms, getKpis } from '../reducers/farmsSlice';
import { checkFeatureFlag } from '../reducers/featureFlagSlice';
import logsFilterMenuSaga from '../components/LogsFilterMenu/logsFilterMenuSaga';
import logsContentSaga from '../components/LogsContentArea/logsContentSaga';
import logsDownloadSaga from '../components/LogsFilterMenu/logsDownloadSaga';
import logsFilterDownloadModalSaga from '../components/LogsFilterMenu/logsFilterDownloadModalSaga';
import { getVdeEventsSaga } from '../containers/EventsPageVDE/sagas/saga';
import {
  getFarmDetails,
  getFarmEntitiesState,
} from '../reducers/farmDetailsSlice';
import entitiesStateSaga from '../components/FarmDetails/entitiesStateSaga';
import {
  addFarmToFavourites,
  getFavouriteFarms,
  removeFarmFromFavourites,
} from '../reducers/favouriteFarmsSlice';
import {
  addFarmToFavouritesSaga,
  getFavouriteFarmsSaga,
  removeFarmFromFavouritesSaga,
} from '../components/FavouriteFarms/saga';
import { getEntityInfoRequest } from '../reducers/entityInformationSlice';
import getUserDetailsSaga from '../common/components/UserDetails/getUserDetailsSaga';
import getLanguagesRequest from '../common/components/Translation/getLanguagesSaga';
import { getLanguages } from '../reducers/translationSlice';

/** Root saga.
 * @return {Object} - return store
 */
function* rootSaga() {
  const envVariablesPassed = process.env;
  const doUseMocks = envVariablesPassed.REACT_APP_USE_MOCKS === 'true';
  if (doUseMocks) {
    yield all([
      logoutSaga(mockLogoutApi),
      updateHistorySaga(mockUpdateHistoryApi),
    ]);
  } else {
    yield all([
      logoutSaga(logoutApi),
      takeLatest(getFarms.type, farmsSaga),
      takeLatest(getKpis.type, fetchKpisSaga),
      updatesSaga(updatesApi),
      takeLatest(getFarmDetails, handleFarmDetails, farmDetailsApi),
      alarmTablesSaga(alarmsApi),
      eventPageDetailsSaga(),
      takeLatest(fetchMoreEvents.type, getMoreEvents),
      takeLatest(getVdeEvents.type, getVdeEventsSaga),
      selectedEventPageDetailsSaga(eventsPageApi),
      domainsSaga(domainsApi),
      domainComponentsSaga(domainsApi),
      logsFilterMenuSaga(logsApi),
      logsContentSaga(logsApi),
      logsDownloadSaga(logsApi),
      logsFilterDownloadModalSaga(logsApi),
      takeLatest(
        getEntityInfoRequest.type,
        getEntityInformation,
        entityInfoApi,
      ),
      entitySettingsSaga(settingsApi),
      entitySettingsOptionsSaga(settingsApi),
      takeLatest(setNetworkError.type, errorCodeSaga),
      eventDropdownLevelsSaga(eventsApi),
      canbusTroubleshootSaga(troubleshootApi),
      canbusTroubleshootTutorialSaga(troubleshootApi),
      entityStatusSaga(entityStatusApi),
      googleAnalyticTrackingSaga(),
      entityKpiDetailsSaga(entityKpiDetailsApi),
      entityBackupSettingsSaga(backupsApi),
      createEntityBackupSettingsSaga(backupsApi),
      getBackupProgressSaga(backupsApi),
      updateEntityBackupSettingsSaga(backupsApi),
      restoreEntityBackupSettingsSaga(backupsApi),
      dataChartSaga(dataChartsApi),
      dataChartEventsSaga(dataChartsApi),
      dataChartConfigurationSaga(dataChartsApi),
      vectorDataChartSaga(dataChartsApi),
      vectorConfigurationSaga(vectorConfigurationApi),
      applicationCacheSaga(),
      takeLatest(checkFeatureFlag.type, featureFlagSaga),
      updateLanguageSaga(updateLelyPreferredLanguage),
      takeLatest(getLanguages.type, getLanguagesRequest), // TODO: Remove this once we have a way to get the languages from the user details
      postLoginActions(),
      eventsLevelMenuOptionsSaga(dataChartsApi),
      compositeStateSaga(compositeStatesApi),
      updateHistorySaga(updateHistoryApi),
      updateHistorySagaForSystem(updateHistoryApi),
      milkJarSaga(dataChartsApi),
      kpiSaga(kpiApi),
      takeLatest(getFarmEntitiesState.type, entitiesStateSaga, farmDetailsApi),
      takeLatest(getFavouriteFarms.type, getFavouriteFarmsSaga),
      takeLatest(addFarmToFavourites.type, addFarmToFavouritesSaga),
      takeLatest(removeFarmFromFavourites.type, removeFarmFromFavouritesSaga),
      translationSaga(),
      getUserDetailsSaga(userDetailsApi),
    ]);
  }
}

export default rootSaga;
